import React, { useEffect, useRef, useState } from 'react'
import { useRefinementList } from 'react-instantsearch-hooks-web'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { cx } from 'class-variance-authority'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { buttonClassName } from '../ui/button/utils'

export type RefinementListProps = {
  name: string
  attribute: string
  // eslint-disable-next-line react/no-unused-prop-types
  type: 'list' | 'range'
  // eslint-disable-next-line no-unused-vars
  transformLabel?: (value: string) => string
  searchable?: boolean
}

const Counter = ({ count = 0 }: { count: number }) => {
  if (count === 0) {
    return null
  }

  return (
    <span className="absolute px-[6px] font-semibold text-sm text-center text-white rounded-full -right-1 -top-2 bg-night">
      {count}
    </span>
  )
}

const RefinementList: React.FC<RefinementListProps> = ({
  attribute: recievedAttribute,
  name,
  transformLabel = undefined,
  searchable = false,
}) => {
  const [openBottom, setOpenBottom] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const attribute = Array.isArray(recievedAttribute)
    ? recievedAttribute[0]
    : recievedAttribute

  const { items, refine, searchForItems, hasExhaustiveItems } =
    useRefinementList({ attribute, limit: 50 })

  const hasRefinements = items.some((item) => item.isRefined)
  const closeDrawerBottom = () => setOpenBottom(false)

  function useOutsideAlerter(ref: React.RefObject<HTMLDivElement>) {
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event?.target)) {
          closeDrawerBottom()
        }
      }
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }

  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef)

  return (
    <div id={`refinement-${name}`} className="flex items-end h-12 mr-1">
      <button
        type="button"
        className={cx(
          'relative md:text-lg',
          buttonClassName({
            color: 'primary',
            invert: hasRefinements,
            filter: true,
            disabled: false,
            size: 'small',
          }),
        )}
        onClick={() => setOpenBottom(!openBottom)}
      >
        <div className="space-x-2 text-sm">
          <span>{name}</span>
          <ChevronDownIcon className="inline-flex w-4 h-4" />
        </div>
        <Counter count={items.filter((item) => item.isRefined).length} />
      </button>

      <section
        id={`refinement-${name}-list`}
        ref={wrapperRef}
        className={cx(
          'p-4 fixed bg-white bottom-0 left-0 right-0 z-20 max-h-[500px] overflow-y-scroll rounded-t-lg animate-enter',
          'md:top-[60px] md:absolute md:bottom-auto md:w-[500px] md:right-auto md:left-auto md:rounded-lg md:shadow-lg md:no-scrollbar',
          openBottom ? 'block' : 'hidden',
        )}
      >
        <div className="relative cursor-pointer">
          <button
            type="button"
            onClick={closeDrawerBottom}
            className="absolute top-0 right-0"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className='pr-6'>
          {searchable && (
            <input
              className="w-full p-2 border-0 outline-0 rounded-xl placeholder:pl-2"
              placeholder="Recherche"
              aria-label="Recherche"
              type="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={512}
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.currentTarget.value)
                searchForItems(event.currentTarget.value)
              }}
            />
          )}
        </div>
        <ul className="mb-12 min-h-fit md:mb-0">
          {items.length > 0 ? (
            items.map((item) => (
              <li key={item.label} className="px-2 py-1 hover:bg-light-blue">
                <label
                  htmlFor={`checkbox-${item.label}`}
                  className="flex items-center gap-2 hover:cursor-pointer"
                >
                  <input
                    id={`checkbox-${item.label}`}
                    type="checkbox"
                    checked={item.isRefined}
                    onChange={() => refine(item.value)}
                    className="rounded-md focus:outline-none hover:cursor-pointer hover:outline-none active:outline-none checked:outline-none text-night border-night"
                  />
                  <span className="text-night">
                    {transformLabel ? transformLabel(item.label) : item.label}
                  </span>
                </label>
              </li>
            ))
          ) : (
            <li>Pas de résultats</li>
          )}
          {!hasExhaustiveItems && <li className="">...</li>}
        </ul>
      </section>
    </div>
  )
}

export default RefinementList
